<template>
  <AccordionItem :value="itemId" class="accordion-item">
    <AccordionHeader class="accordion-header">
      <AccordionTrigger class="accordion-button collapsed" type="button">
        <div class="title">
          <span v-if="startIcon" class="start" v-html="startIcon" />
          {{ title }}
        </div>
      </AccordionTrigger>
    </AccordionHeader>
    <AccordionContent class="accordion-collapse">
      <div class="accordion-body">
        <slot />
      </div>
    </AccordionContent>
  </AccordionItem>
</template>

<script setup lang="ts">
import { useId } from 'vue'
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from 'reka-ui'

// Accordion item powered by Reka UI primitives. Replaces the previous
// implementation that imported `bootstrap/js/dist/collapse` (forbidden in
// the Vue 3 workspace per .agents/mobile_and_web_app.md).
//
// Bootstrap CSS classes (.accordion-item, .accordion-button, .accordion-body)
// are preserved so the visual shell matches Slim-rendered accordions exactly.
// Two stateful classes from Bootstrap need special handling because Bootstrap
// JS isn't available to toggle them:
//   - `.accordion-button.collapsed` → kept STATIC. The <style scoped> block
//     below uses Reka's `[data-state="open"]` attribute to override the
//     collapsed look (chevron rotation + active color) when the item is open.
//   - `.accordion-collapse.collapse` → the `.collapse` modifier is dropped.
//     `.collapse` carries `display:none`, which Bootstrap JS normally toggles
//     to `.show`. Reka's AccordionContent already renders the body only when
//     open (via <Presence>), so the modifier would just permanently hide it.
//
// Exclusive-vs-multiple mode is owned by AccordionGroup (Reka's `type` prop
// on AccordionRoot).

const itemId = `acc-item-${useId()}`

defineProps<{
  title: string
  startIcon?: string | null
}>()
</script>

<style scoped>
/* Mirror Bootstrap's :not(.collapsed) look when Reka says the trigger is open.
   Variables match the Bootstrap accordion mixin in @twg/design-tokens (which
   compiles the same Bootstrap 5.2.3 source the Rails app uses). The button
   itself gets a short color/background transition so the active state fades
   in instead of snapping. */
.accordion-button {
  transition:
    color 0.15s ease-in-out,
    background-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
}
.accordion-button::after {
  transition: transform 0.2s ease-in-out, background-image 0.15s ease-in-out;
}

.accordion-button.collapsed[data-state='open'] {
  color: var(--bs-accordion-active-color);
  background-color: var(--bs-accordion-active-bg);
  box-shadow: inset 0 calc(-1 * var(--bs-accordion-border-width)) 0 var(--bs-accordion-border-color);
}

.accordion-button.collapsed[data-state='open']::after {
  background-image: var(--bs-accordion-btn-active-icon);
  transform: var(--bs-accordion-btn-icon-transform);
}

/* Body slide animation. Bootstrap normally toggles .collapsing/.collapse.show
   with JS-driven height; Reka manages state via [data-state] and exposes the
   measured content height via --reka-accordion-content-height. Drive a CSS
   keyframe animation from that variable so open/close slide smoothly. */
.accordion-collapse {
  overflow: hidden;
}
.accordion-collapse[data-state='open'] {
  animation: tw-accordion-down 0.2s ease-out;
}
.accordion-collapse[data-state='closed'] {
  animation: tw-accordion-up 0.2s ease-out;
}

@keyframes tw-accordion-down {
  from { height: 0; }
  to { height: var(--reka-accordion-content-height); }
}
@keyframes tw-accordion-up {
  from { height: var(--reka-accordion-content-height); }
  to { height: 0; }
}
</style>
